import React from 'react';
import ReactDOM from 'react-dom';
import PaginatedTable from './PaginatedTable';

interface ActivityLog {
  userId: string;
  timestamp: string;
  action: string;
  details: Record<string, unknown>;
}

interface ActivityLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  activityLogs: ActivityLog[];
  loading: boolean;
}

const ActivityLogsModal: React.FC<ActivityLogsModalProps> = ({
  isOpen,
  onClose,
  userId,
  activityLogs,
  loading
}) => {
  if (!isOpen) return null;

  const columns = [
    {
      key: 'action',
      header: 'Action',
      sortable: true,
      render: (log: ActivityLog) => (
        <span className="text-sm font-medium text-gray-900">{log.action}</span>
      )
    },
    {
      key: 'details',
      header: 'Details',
      render: (log: ActivityLog) => (
        <div className="text-sm text-gray-500 max-w-xs">
          <div className="max-w-xs overflow-hidden break-words whitespace-pre-wrap text-xs" title={JSON.stringify(log.details, null, 2)}>
            {JSON.stringify(log.details, null, 2)}
          </div>
        </div>
      )
    },
    {
      key: 'timestamp',
      header: 'Timestamp',
      sortable: true,
      render: (log: ActivityLog) => (
        <span className="text-sm text-gray-500">
          {new Date(log.timestamp).toLocaleString()}
        </span>
      )
    }
  ];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">User Activity Logs</h3>
            <p className="text-sm text-gray-500 mt-1">Activity history for user: {userId}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <PaginatedTable
            data={activityLogs || []}
            columns={columns}
            loading={loading}
            pageSize={20}
            searchPlaceholder="Search activity logs by action, details..."
            emptyMessage="No activity logs found for this user"
            maxHeight="500px"
            showPageSizeSelector={true}
            pageSizeOptions={[10, 20, 50, 100]}
            className="border-0"
            defaultSort={{ key: 'timestamp', direction: 'desc' }}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  , document.body);
};

export default ActivityLogsModal;